/**
 * Dates as a person in Belgium reads them.
 *
 * `new Date().toISOString().slice(0, 10)` looks like "today" and is not. It is
 * today **in UTC**, and Belgium runs an hour or two ahead — so between midnight
 * and 02:00 local it quietly answers *yesterday*. Fourteen places in this app
 * did that, including the date written onto a payment: pay a bill at half past
 * midnight and the ledger recorded it as the day before.
 *
 * The month-end version is worse, because it is wrong all day long.
 * `new Date(2026, 9, 0)` is local midnight on 30 September, which in UTC+2 is
 * 22:00 on the 29th — so `.toISOString()` hands back the 29th. Every "last day
 * of the month" boundary was one day short, and an expense on the 30th simply
 * never appeared in September's total.
 *
 * Rule 2 says a due date has no timezone. That is exactly why it must never be
 * derived from one: read the calendar the browser is showing, not the clock
 * Greenwich is keeping.
 */

/** The date a Belgian calendar on this device shows. Never UTC. */
export function isoLocal(d: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** Today. The one that means "the day I am in". */
export function todayIso(now: Date = new Date()): string {
  return isoLocal(now)
}

/** Days from today, staying on the local calendar the whole way. */
export function daysFromToday(days: number, now: Date = new Date()): string {
  return isoLocal(new Date(now.getFullYear(), now.getMonth(), now.getDate() + days))
}

/**
 * The last day of a month, as `YYYY-MM`.
 *
 * Built and read locally throughout. Going through UTC here is the bug that
 * dropped the 30th of every month out of its own month.
 */
export function lastDayOfMonth(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number]
  return isoLocal(new Date(y, m, 0))
}
